import { audio } from '../../../audio/AudioManager.js';
import { SaveManager } from '../../../state/SaveManager.js';
import { applicationLifecycle } from '../../shared/lifecycle/index.js';
import { ensureMinimumTextTouchTarget } from '../../ui/TouchTarget.js';
import {
  resolveStoryForestLayout,
  STORY_FOREST_LAYOUT_HEIGHT,
  STORY_FOREST_LAYOUT_WIDTH,
} from './components/StoryForestLayout.js';
import { CHARACTER_GROVE_RESPONSE_1 } from './content/CharacterGroveStories.js';
import { STORY_FOREST_ASSETS } from './StoryForestAssets.js';
import {
  completeCharacterGroveChallenge,
  getStoryForestState,
  markCharacterGroveDiscovered,
  markMemoryTreeDiscovered,
  saveCharacterGroveChallengeSnapshot,
  STORY_FOREST_DISTRICTS,
  visitStoryForestDistrict,
} from './StoryForestState.js';
import {
  CHARACTER_GROVE_ASSETS,
  CHARACTER_GROVE_ASSET_LIST,
  canEnterCharacterGrove,
  getMissingCharacterGroveAssets,
} from './minigames/character-grove/CharacterGroveAssets.js';
import { CharacterGroveChallenge } from './minigames/character-grove/CharacterGroveChallenge.js';
import type { CharacterGroveSnapshot } from './minigames/character-grove/CharacterGroveState.js';
import { canEnterSequenceStream } from './sequence-stream/SequenceStreamAssets.js';

export const CHARACTER_GROVE_SCENE_KEY = 'CharacterGrove';

const FONT_READING = '"Nunito", "Segoe UI", Arial, sans-serif';

interface ChoiceView {
  readonly id: string;
  readonly image: Phaser.GameObjects.Image;
  readonly label: Phaser.GameObjects.Text;
  readonly shortcut: Phaser.GameObjects.Text;
}

interface ArtButton {
  readonly image: Phaser.GameObjects.Image;
  readonly label: Phaser.GameObjects.Text;
}

export class CharacterGroveScene extends Phaser.Scene {
  static readonly requiredImageKeys = CHARACTER_GROVE_ASSET_LIST.map(asset => asset.key);

  private challenge!: CharacterGroveChallenge;
  private background!: Phaser.GameObjects.Image;
  private grovePath!: Phaser.GameObjects.Image;
  private foregroundLeft!: Phaser.GameObjects.Image;
  private foregroundRight!: Phaser.GameObjects.Image;
  private treeDormant!: Phaser.GameObjects.Image;
  private treeRestored!: Phaser.GameObjects.Image;
  private storyPanel!: Phaser.GameObjects.Image;
  private insightPanel!: Phaser.GameObjects.Image;
  private evidencePrompt!: Phaser.GameObjects.Image;
  private evidenceLink!: Phaser.GameObjects.Image;
  private evidenceSlot!: Phaser.GameObjects.Image;
  private restoredBranch!: Phaser.GameObjects.Image;
  private cosmo!: Phaser.GameObjects.Image;
  private titleText!: Phaser.GameObjects.Text;
  private phaseText!: Phaser.GameObjects.Text;
  private storyTitle!: Phaser.GameObjects.Text;
  private storyText!: Phaser.GameObjects.Text;
  private pageText!: Phaser.GameObjects.Text;
  private previousText!: Phaser.GameObjects.Text;
  private nextText!: Phaser.GameObjects.Text;
  private characterText!: Phaser.GameObjects.Text;
  private feedbackText!: Phaser.GameObjects.Text;
  private controlsText!: Phaser.GameObjects.Text;
  private homeText!: Phaser.GameObjects.Text;
  private gardenText!: Phaser.GameObjects.Text;
  private streamText!: Phaser.GameObjects.Text;
  private hintButton!: ArtButton;
  private resetButton!: ArtButton;
  private connectButton!: ArtButton;
  private insightViews: ChoiceView[] = [];
  private evidenceViews: ChoiceView[] = [];
  private reducedMotion = false;
  private inputEnabled = true;
  private leaving = false;
  private renderScale = 1;
  private resizeTimer?: number;
  private unsubscribeLifecycle?: () => void;

  constructor() { super({ key: CHARACTER_GROVE_SCENE_KEY }); }

  create() {
    this.insightViews = [];
    this.evidenceViews = [];
    this.inputEnabled = true;
    this.leaving = false;

    const state = getStoryForestState();
    const missing = getMissingCharacterGroveAssets(this.textures);
    if (!state.unlockedDistricts.includes(STORY_FOREST_DISTRICTS.characterGrove)
      || !canEnterCharacterGrove(this.textures)) {
      if (missing.length > 0) {
        console.error('[CharacterGrove] Required production textures are missing:',
          missing.map(asset => `${asset.key} (${asset.path})`));
      }
      this.scene.start('WordGarden');
      return;
    }

    void audio.enterSoundscape('story-forest');
    this.renderScale = this.configureRenderer();
    this.reducedMotion = SaveManager.getSettings().reducedMotion
      || (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false);
    this.inputEnabled = true;
    this.leaving = false;
    markCharacterGroveDiscovered();
    markMemoryTreeDiscovered();
    SaveManager.setLastScene(CHARACTER_GROVE_SCENE_KEY);
    SaveManager.save();

    this.buildEnvironment();
    this.buildInterface();
    const saved = state.characterGroveChallengeSnapshots[CHARACTER_GROVE_RESPONSE_1.id];
    this.challenge = new CharacterGroveChallenge(
      CHARACTER_GROVE_RESPONSE_1,
      {
        onStateChanged: snapshot => {
          saveCharacterGroveChallengeSnapshot(snapshot);
          this.refresh(snapshot);
        },
        onHint: hint => {
          this.feedbackText.setText(hint.message);
          audio.hint();
        },
        onIncorrect: (_result, clue) => {
          this.feedbackText.setText(clue);
          audio.wrong();
        },
        onCompleted: (config, snapshot) => this.handleComplete(config.completionWorldEvent, snapshot),
      },
      saved,
    );
    this.registerInput();
    this.registerLifecycle();
    this.layout();
    this.refresh(this.challenge.getSnapshot());
    this.applyTextResolution();
    this.cameras.main.fadeIn(this.reducedMotion ? 160 : 460, 100, 180, 100);
  }

  private buildEnvironment() {
    const assets = CHARACTER_GROVE_ASSETS;
    this.background = this.add.image(640, 360, assets.background.key)
      .setDisplaySize(1280, 720).setDepth(0);
    this.grovePath = this.add.image(640, 720, assets.grovePath.key)
      .setOrigin(0.5, 1).setDepth(3);
    this.foregroundLeft = this.add.image(0, 720, assets.foregroundLeft.key)
      .setOrigin(0, 1).setDepth(90);
    this.foregroundRight = this.add.image(1280, 720, assets.foregroundRight.key)
      .setOrigin(1, 1).setDepth(90);
    this.treeDormant = this.add.image(1050, 680, assets.memoryTreeDormant.key)
      .setOrigin(0.5, 1).setDepth(10);
    this.treeRestored = this.add.image(1050, 680, assets.memoryTreeRestored.key)
      .setOrigin(0.5, 1).setDepth(11).setAlpha(0);
    this.cosmo = this.add.image(105, 650, STORY_FOREST_ASSETS.storyForestCosmo.key)
      .setDepth(95);
  }

  private buildInterface() {
    const assets = CHARACTER_GROVE_ASSETS;
    this.storyPanel = this.add.image(330, 255, assets.storyCharacterPanel.key).setDepth(20);
    this.insightPanel = this.add.image(760, 265, assets.insightPanel.key).setDepth(20);
    this.evidencePrompt = this.add.image(650, 560, assets.evidencePrompt.key).setDepth(20);
    this.evidenceLink = this.add.image(760, 485, assets.evidenceLink.key).setDepth(24);
    this.evidenceSlot = this.add.image(760, 515, assets.evidenceSlot.key).setDepth(25);
    this.restoredBranch = this.add.image(820, 500, assets.restoredBranch.key)
      .setDepth(26).setVisible(false);

    this.titleText = this.makeText('CHARACTER GROVE', 29, '#fff2b8', 'bold')
      .setStroke('#173f2b', 7).setDepth(110);
    this.phaseText = this.makeText('', 14, '#eaffd6', 'bold')
      .setStroke('#173f2b', 5).setDepth(110);
    this.storyTitle = this.makeText(CHARACTER_GROVE_RESPONSE_1.story.title, 22, '#4f3322', 'bold')
      .setDepth(30);
    this.storyText = this.makeText('', 19, '#3e3028', 'normal')
      .setOrigin(0, 0).setAlign('left').setLineSpacing(7).setDepth(30);
    this.pageText = this.makeText('', 13, '#6e4a2d', 'bold').setDepth(30);
    this.previousText = this.makeText('← PREVIOUS', 14, '#5d3a22', 'bold')
      .setInteractive({ useHandCursor: true }).setDepth(31);
    this.nextText = this.makeText('NEXT →', 14, '#5d3a22', 'bold')
      .setInteractive({ useHandCursor: true }).setDepth(31);
    this.characterText = this.makeText('', 15, '#fff8df', 'bold')
      .setStroke('#173f2b', 5).setWordWrapWidth(310).setDepth(111);
    this.feedbackText = this.makeText('', 15, '#fff8df', 'bold')
      .setStroke('#173f2b', 5).setWordWrapWidth(720).setDepth(111);
    this.controlsText = this.makeText(
      '←/→ pages  •  1–3 insight  •  Q/W/E/V evidence  •  Enter act  •  H hint  •  R reset  •  Esc garden',
      12,
      '#fff8df',
      'bold',
    ).setStroke('#173f2b', 5).setDepth(111);
    this.homeText = this.makeText('HOME HUB  (M)', 15, '#ffffff', 'bold')
      .setStroke('#173f2b', 6).setInteractive({ useHandCursor: true }).setDepth(112);
    this.gardenText = this.makeText('←  WORD GARDEN', 15, '#ffffff', 'bold')
      .setStroke('#173f2b', 6).setInteractive({ useHandCursor: true }).setDepth(112);
    this.streamText = this.makeText('SEQUENCE STREAM  →', 15, '#fff2b8', 'bold')
      .setStroke('#173f2b', 6).setInteractive({ useHandCursor: true }).setDepth(112);

    [this.previousText, this.nextText, this.homeText, this.gardenText, this.streamText]
      .forEach(target => ensureMinimumTextTouchTarget(target));
    this.previousText.on('pointerup', () => this.previousPage());
    this.nextText.on('pointerup', () => this.advance());
    this.homeText.on('pointerup', this.returnHome, this);
    this.gardenText.on('pointerup', this.returnGarden, this);
    this.streamText.on('pointerup', this.travelToSequenceStream, this);

    this.insightViews = CHARACTER_GROVE_RESPONSE_1.insightChoices.map((choice, index) => {
      const image = this.add.image(0, 0, assets.insightToken.key)
        .setDepth(35).setInteractive({ useHandCursor: true });
      const label = this.makeText(choice.text, 13, '#fff8df', 'bold')
        .setStroke('#173f2b', 4).setWordWrapWidth(150).setDepth(36);
      const shortcut = this.makeText(String(index + 1), 13, '#fff5a6', 'bold')
        .setStroke('#173f2b', 4).setDepth(37);
      image.on('pointerup', () => this.selectInsight(choice.id));
      return { id: choice.id, image, label, shortcut };
    });
    this.evidenceViews = CHARACTER_GROVE_RESPONSE_1.evidenceMoments.map((moment, index) => {
      const image = this.add.image(0, 0, assets.memoryEventToken.key)
        .setDepth(38).setInteractive({ useHandCursor: true });
      const label = this.makeText(moment.text, 12, '#fff8df', 'bold')
        .setStroke('#173f2b', 4).setWordWrapWidth(155).setDepth(39);
      const shortcut = this.makeText(['Q', 'W', 'E', 'V'][index], 12, '#fff5a6', 'bold')
        .setStroke('#173f2b', 4).setDepth(40);
      image.on('pointerup', () => this.selectEvidence(moment.id));
      return { id: moment.id, image, label, shortcut };
    });

    this.hintButton = this.makeButton(assets.hintButton.key, 'HINT', () => this.hint());
    this.resetButton = this.makeButton(assets.resetButton.key, 'RESET', () => this.reset());
    this.connectButton = this.makeButton(assets.connectButton.key, 'CONNECT', () => this.advance());
  }

  private makeText(value: string, size: number, color: string, style: string) {
    return this.add.text(0, 0, value, {
      fontFamily: FONT_READING,
      fontSize: `${size}px`,
      fontStyle: style,
      color,
      align: 'center',
    }).setOrigin(0.5);
  }

  private makeButton(texture: string, label: string, action: () => void): ArtButton {
    const image = this.add.image(0, 0, texture).setDepth(105).setInteractive({ useHandCursor: true });
    const text = this.makeText(label, 13, '#fff8df', 'bold').setStroke('#173f2b', 4).setDepth(106);
    image.on('pointerup', action);
    return { image, label: text };
  }

  private advance() {
    if (!this.inputEnabled || this.leaving) return;
    const snapshot = this.challenge.getSnapshot();
    if (snapshot.phase === 'read') {
      if (snapshot.storyPageIndex < CHARACTER_GROVE_RESPONSE_1.story.pages.length - 1) {
        this.challenge.nextStoryPage();
      } else if (this.challenge.finishReading()) {
        this.playMemoryReveal();
      }
    } else if (snapshot.phase === 'inspect-character') {
      this.challenge.inspectCharacter();
    } else if (snapshot.phase === 'connect') {
      const result = this.challenge.connect();
      if (result === 'correct') this.playEvidenceConnection();
      else if (result === 'incomplete') this.feedbackText.setText('Choose one insight and one story moment first.');
    }
    audio.click();
  }

  private previousPage() {
    if (this.challenge.getSnapshot().phase === 'read') this.challenge.previousStoryPage();
  }

  private selectInsight(id: string) {
    if (this.challenge.selectInsight(id)) audio.click();
  }

  private selectEvidence(id: string) {
    if (this.challenge.selectEvidence(id)) audio.click();
  }

  private hint() { this.challenge.requestHint(); }

  private reset() {
    this.challenge.reset();
    saveCharacterGroveChallengeSnapshot(this.challenge.getSnapshot());
    this.feedbackText.setText('Story memory reset. Read again and build a new evidence link.');
    audio.click();
  }

  private refresh(snapshot: CharacterGroveSnapshot) {
    const config = CHARACTER_GROVE_RESPONSE_1;
    const page = config.story.pages[snapshot.storyPageIndex];
    this.storyText.setText(page.text);
    this.pageText.setText(`PAGE ${snapshot.storyPageIndex + 1} OF ${config.story.pages.length}`);
    this.previousText.setVisible(snapshot.phase === 'read' && snapshot.storyPageIndex > 0);
    this.nextText.setVisible(snapshot.phase === 'read')
      .setText(snapshot.storyPageIndex === config.story.pages.length - 1 ? 'FINISH STORY →' : 'NEXT →');
    this.phaseText.setText(snapshot.completed
      ? 'MEMORY TREE RESTORED'
      : snapshot.phase.replace(/-/g, ' ').toLocaleUpperCase());
    this.characterText.setText(snapshot.phase === 'read'
      ? 'Read Mira’s story. What does she do when the crossing feels difficult?'
      : `${config.character.name}: ${config.character.description}\n${config.character.inspectPrompt}`);

    const choicesVisible = ['choose-insight', 'find-supporting-moment', 'connect', 'complete']
      .includes(snapshot.phase);
    this.insightPanel.setVisible(choicesVisible);
    this.insightViews.forEach(view => {
      const selected = view.id === snapshot.selectedInsightId;
      view.image.setVisible(choicesVisible)
        .setTexture(selected ? CHARACTER_GROVE_ASSETS.insightTokenSelected.key : CHARACTER_GROVE_ASSETS.insightToken.key);
      view.label.setVisible(choicesVisible);
      view.shortcut.setVisible(choicesVisible);
    });

    const evidenceVisible = ['find-supporting-moment', 'connect', 'complete'].includes(snapshot.phase);
    this.evidencePrompt.setVisible(evidenceVisible);
    this.evidenceLink.setVisible(evidenceVisible && !snapshot.completed);
    this.evidenceSlot.setVisible(evidenceVisible && !snapshot.completed);
    this.evidenceViews.forEach(view => {
      const selected = snapshot.selectedEvidenceIds.includes(view.id);
      view.image.setVisible(evidenceVisible).setTint(selected ? 0xfff39a : 0xffffff);
      view.label.setVisible(evidenceVisible);
      view.shortcut.setVisible(evidenceVisible);
    });

    const restored = snapshot.completed;
    this.streamText.setVisible(restored);
    this.treeDormant.setAlpha(restored ? 0 : 1);
    this.treeRestored.setAlpha(restored ? 1 : 0);
    this.restoredBranch.setVisible(restored);
    this.connectButton.image.setVisible(!restored);
    this.connectButton.label.setVisible(!restored).setText(
      snapshot.phase === 'read' ? 'READ ON'
        : snapshot.phase === 'inspect-character' ? 'INSPECT MIRA'
          : 'CONNECT',
    );
    this.hintButton.image.setVisible(!restored);
    this.hintButton.label.setVisible(!restored);
    this.resetButton.image.setVisible(!restored);
    this.resetButton.label.setVisible(!restored);
    if (restored) {
      this.feedbackText.setText(config.successMessage);
      this.characterText.setText('Mira felt nervous and still found a careful way to help. Evidence restored the memory.');
    } else if (!this.feedbackText.text) {
      this.feedbackText.setText('Read, choose an insight, then connect it to the strongest story moment.');
    }
    this.layout();
  }

  private playMemoryReveal() {
    const effect = this.add.image(this.treeDormant.x, this.treeDormant.y - 190, CHARACTER_GROVE_ASSETS.memoryRevealVfx.key)
      .setDisplaySize(310, 310).setDepth(75).setBlendMode(Phaser.BlendModes.ADD)
      .setAlpha(this.reducedMotion ? 0.65 : 0);
    if (this.reducedMotion) this.time.delayedCall(700, () => effect.destroy());
    else this.tweens.add({ targets: effect, alpha: 0.85, duration: 420, yoyo: true, hold: 120, onComplete: () => effect.destroy() });
  }

  private playEvidenceConnection() {
    const effect = this.add.image(this.evidenceLink.x, this.evidenceLink.y, CHARACTER_GROVE_ASSETS.evidenceConnectionVfx.key)
      .setDisplaySize(360, 180).setDepth(76).setBlendMode(Phaser.BlendModes.ADD)
      .setAlpha(this.reducedMotion ? 0.72 : 0);
    if (this.reducedMotion) this.time.delayedCall(800, () => effect.destroy());
    else this.tweens.add({ targets: effect, alpha: 0.9, duration: 450, yoyo: true, hold: 160, onComplete: () => effect.destroy() });
  }

  private handleComplete(restorationEvent: string, snapshot: CharacterGroveSnapshot) {
    const result = completeCharacterGroveChallenge(
      CHARACTER_GROVE_RESPONSE_1.id,
      restorationEvent,
      CHARACTER_GROVE_RESPONSE_1.skillKind,
    );
    saveCharacterGroveChallengeSnapshot(snapshot);
    this.refresh(snapshot);
    audio.restoration();
    const effect = this.add.image(this.treeRestored.x, this.treeRestored.y - 190, CHARACTER_GROVE_ASSETS.treeRestorationVfx.key)
      .setDisplaySize(390, 390).setDepth(78).setBlendMode(Phaser.BlendModes.ADD)
      .setAlpha(this.reducedMotion ? 0.75 : 0);
    if (this.reducedMotion) this.time.delayedCall(1000, () => effect.destroy());
    else this.tweens.add({
      targets: effect,
      alpha: 0.94,
      scaleX: 1.15,
      scaleY: 1.15,
      duration: 650,
      yoyo: true,
      hold: 220,
      onComplete: () => effect.destroy(),
    });
    if (result.firstCompletion) audio.celebrate();
  }

  private registerInput() { this.input.keyboard?.on('keydown', this.handleKeyDown, this); }

  private handleKeyDown(event: KeyboardEvent) {
    if (!this.inputEnabled || this.leaving) return;
    const key = event.key.toLocaleLowerCase();
    if (key === 'escape') return this.returnGarden();
    if (key === 'm') return this.returnHome();
    if (key === 'arrowleft') return this.previousPage();
    if (key === 'arrowright' || key === 'enter' || key === ' ') return this.advance();
    if (key >= '1' && key <= '3') {
      const choice = CHARACTER_GROVE_RESPONSE_1.insightChoices[Number(key) - 1];
      if (choice) this.selectInsight(choice.id);
      return;
    }
    const evidenceIndex = ['q', 'w', 'e', 'v'].indexOf(key);
    if (evidenceIndex >= 0) {
      const evidence = CHARACTER_GROVE_RESPONSE_1.evidenceMoments[evidenceIndex];
      if (evidence) this.selectEvidence(evidence.id);
      return;
    }
    if (key === 'h') return this.hint();
    if (key === 's') return this.travelToSequenceStream();
    if (key === 'r') this.reset();
  }

  private layout() {
    const { safe, compact } = resolveStoryForestLayout(this);
    this.challenge?.layout({ left: safe.x, right: safe.right, top: safe.y, bottom: safe.bottom });
    this.titleText.setPosition(safe.centerX, safe.y + (compact ? 26 : 34)).setFontSize(compact ? 23 : 29);
    this.phaseText.setPosition(safe.centerX, safe.y + (compact ? 52 : 64));
    this.gardenText.setPosition(safe.x + 82, safe.y + 26).setFontSize(compact ? 12 : 15);
    this.streamText.setPosition(safe.right - (compact ? 160 : 190), safe.y + (compact ? 58 : 66))
      .setFontSize(compact ? 12 : 15);
    this.homeText.setPosition(safe.right - 66, safe.y + 26).setFontSize(compact ? 12 : 15);

    const storyW = compact ? 450 : 500;
    const storyH = storyW * (640 / 960);
    const storyX = safe.x + (compact ? 250 : 280);
    const storyY = safe.y + (compact ? 215 : 245);
    this.storyPanel.setPosition(storyX, storyY).setDisplaySize(storyW, storyH);
    this.storyTitle.setPosition(storyX, storyY - storyH * 0.33).setFontSize(compact ? 18 : 22);
    this.storyText.setPosition(storyX - storyW * 0.37, storyY - storyH * 0.20)
      .setFontSize(compact ? 16 : 19).setWordWrapWidth(storyW * 0.74);
    this.pageText.setPosition(storyX, storyY + storyH * 0.34);
    this.previousText.setPosition(storyX - storyW * 0.27, storyY + storyH * 0.40);
    this.nextText.setPosition(storyX + storyW * 0.27, storyY + storyH * 0.40);

    const treeHeight = compact ? 280 : 360;
    const treeWidth = treeHeight * (768 / 1024);
    const treeX = safe.right - (compact ? 130 : 155);
    const treeBottom = safe.y + (compact ? 340 : 465);
    this.treeDormant.setPosition(treeX, treeBottom).setDisplaySize(treeWidth, treeHeight);
    this.treeRestored.setPosition(treeX, treeBottom).setDisplaySize(treeWidth, treeHeight);
    this.grovePath.setPosition(treeX, safe.bottom).setDisplaySize(compact ? 280 : 340, compact ? 210 : 255);
    this.characterText.setPosition(treeX - (compact ? 85 : 115), safe.y + (compact ? 340 : 440))
      .setFontSize(compact ? 12 : 15).setWordWrapWidth(compact ? 265 : 330);

    const insightX = safe.centerX + (compact ? 35 : 65);
    const insightY = safe.y + (compact ? 220 : 255);
    this.insightPanel.setPosition(insightX, insightY)
      .setDisplaySize(compact ? 250 : 285, compact ? 281 : 321);
    this.insightViews.forEach((view, index) => {
      const y = insightY - (compact ? 78 : 92) + index * (compact ? 77 : 88);
      view.image.setPosition(insightX, y).setDisplaySize(compact ? 72 : 82, compact ? 72 : 82);
      view.label.setPosition(insightX, y).setFontSize(compact ? 10 : 12)
        .setWordWrapWidth(compact ? 120 : 145);
      view.shortcut.setPosition(insightX - (compact ? 48 : 56), y - (compact ? 28 : 32));
    });

    const evidenceY = safe.bottom - (compact ? 126 : 136);
    this.evidencePrompt.setPosition(safe.centerX, evidenceY)
      .setDisplaySize(Math.min(compact ? 690 : 760, safe.width - 220), compact ? 188 : 207);
    const evidenceGap = compact ? 152 : 174;
    this.evidenceViews.forEach((view, index) => {
      const x = safe.centerX + (index - 1.5) * evidenceGap;
      view.image.setPosition(x, evidenceY).setDisplaySize(compact ? 94 : 108, compact ? 94 : 108);
      view.label.setPosition(x, evidenceY).setFontSize(compact ? 9 : 11)
        .setWordWrapWidth(compact ? 120 : 145);
      view.shortcut.setPosition(x - (compact ? 47 : 54), evidenceY - (compact ? 42 : 48));
    });
    this.evidenceLink.setPosition(safe.centerX, evidenceY - (compact ? 86 : 98))
      .setDisplaySize(compact ? 300 : 340, compact ? 100 : 113);
    this.evidenceSlot.setPosition(safe.centerX, evidenceY - (compact ? 70 : 82))
      .setDisplaySize(compact ? 86 : 100, compact ? 86 : 100);
    this.restoredBranch.setPosition(safe.centerX + 120, evidenceY - 70)
      .setDisplaySize(compact ? 330 : 380, compact ? 220 : 253);

    const buttonY = safe.bottom - 40;
    this.hintButton.image.setPosition(safe.centerX - 210, buttonY).setDisplaySize(105, 53);
    this.hintButton.label.setPosition(safe.centerX - 210, buttonY);
    this.connectButton.image.setPosition(safe.centerX, buttonY).setDisplaySize(132, 59);
    this.connectButton.label.setPosition(safe.centerX, buttonY);
    this.resetButton.image.setPosition(safe.centerX + 210, buttonY).setDisplaySize(105, 53);
    this.resetButton.label.setPosition(safe.centerX + 210, buttonY);
    this.feedbackText.setPosition(safe.centerX, safe.bottom - (compact ? 202 : 220))
      .setFontSize(compact ? 12 : 14).setWordWrapWidth(Math.min(720, safe.width - 260));
    this.controlsText.setPosition(safe.centerX, safe.bottom - 12).setFontSize(compact ? 9 : 11);
    this.cosmo.setPosition(safe.x + (compact ? 55 : 72), safe.bottom - (compact ? 50 : 60))
      .setDisplaySize(compact ? 78 : 96, compact ? 104 : 128);
    this.foregroundLeft.setPosition(safe.x - 8, safe.bottom).setDisplaySize(compact ? 225 : 260, compact ? 316 : 366);
    this.foregroundRight.setPosition(safe.right + 8, safe.bottom).setDisplaySize(compact ? 225 : 260, compact ? 316 : 366);
  }

  private configureRenderer() {
    const parentW = this.scale.parentSize.width || window.innerWidth;
    const parentH = this.scale.parentSize.height || window.innerHeight;
    const cssScale = Math.min(parentW / STORY_FOREST_LAYOUT_WIDTH, parentH / STORY_FOREST_LAYOUT_HEIGHT);
    const dpr = Phaser.Math.Clamp(window.devicePixelRatio || 1, 1, 2);
    const scale = Phaser.Math.Clamp(cssScale * dpr, 1, 2);
    this.scale.scaleMode = Phaser.Scale.FIT;
    this.scale.autoCenter = Phaser.Scale.CENTER_BOTH;
    this.scale.displaySize.setAspectMode(Phaser.Scale.FIT);
    this.scale.setGameSize(Math.round(STORY_FOREST_LAYOUT_WIDTH * scale), Math.round(STORY_FOREST_LAYOUT_HEIGHT * scale));
    this.scale.refresh();
    this.cameras.main.setZoom(scale).centerOn(640, 360);
    return scale;
  }

  private applyTextResolution() {
    this.children.list.forEach(object => {
      if (object instanceof Phaser.GameObjects.Text) object.setResolution(this.renderScale);
    });
  }

  private registerLifecycle() {
    this.unsubscribeLifecycle = applicationLifecycle.subscribe({
      onSuspend: () => { this.inputEnabled = false; this.challenge?.setSuspended(true); },
      onResume: () => { this.inputEnabled = true; this.challenge?.setSuspended(false); },
    });
    window.addEventListener('resize', this.handleResize);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, this.shutdown, this);
  }

  private readonly handleResize = () => {
    if (this.resizeTimer !== undefined) window.clearTimeout(this.resizeTimer);
    this.resizeTimer = window.setTimeout(() => {
      if (!this.scene.isActive()) return;
      this.renderScale = this.configureRenderer();
      this.layout();
      this.applyTextResolution();
    }, 80);
  };

  private readonly returnGarden = () => {
    if (this.leaving) return;
    this.leaving = true;
    visitStoryForestDistrict(STORY_FOREST_DISTRICTS.wordGarden);
    this.scene.start('WordGarden');
  };

  private readonly returnHome = () => {
    if (this.leaving) return;
    this.leaving = true;
    SaveManager.save();
    this.scene.start('HomeHub');
  };

  private readonly travelToSequenceStream = () => {
    if (this.leaving || !this.challenge.getSnapshot().completed) return;
    if (!canEnterSequenceStream(this.textures)) {
      this.feedbackText.setText('The stream path is open. Sequence Stream is awaiting its runtime QA gate.');
      return audio.click();
    }
    this.leaving = true;
    saveCharacterGroveChallengeSnapshot(this.challenge.getSnapshot());
    this.scene.start('SequenceStream');
  };

  private shutdown() {
    saveCharacterGroveChallengeSnapshot(this.challenge.getSnapshot());
    this.unsubscribeLifecycle?.();
    this.unsubscribeLifecycle = undefined;
    audio.leaveSoundscape({ id: 'story-forest' });
    window.removeEventListener('resize', this.handleResize);
    if (this.resizeTimer !== undefined) window.clearTimeout(this.resizeTimer);
    this.input.keyboard?.off('keydown', this.handleKeyDown, this);
    this.tweens.killAll();
  }
}
